import React, { useState, useMemo } from 'react';
import * as Recharts from 'recharts';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { Dropdown } from 'react-bootstrap';

import ChartCard from '../../ChartComponents/ChartCard';
import ChartTooltip from '../../ChartComponents/ChartTooltip';
import Info from '../../../../components/Info';
import Authorize from '../../../../components/Authorize';
import { CONFIG } from '../../../../components/Authorize/permissions/resources';

import { formatDateToTimezone } from '../../../../helpers/utils';

const GroupMemoryChart = () => {
  const timezone = useSelector(({ events: { currentEvent } }) => currentEvent?.timezone);
  const {
    usersVsSnapshotsTimelines: groupMemoryData,
    usersVsSnapshotsTimelines1m: groupMemoryData1m,
    usersVsSnapshotsTimelines5m: groupMemoryData5m,
    usersVsSnapshotsTimelines30m: groupMemoryData30m,
  } = useSelector(({ analytics: { basic: { data } } }) => data);
  const [showUsers, setShowUsers] = useState(true);
  const [showSnapshots, setShowSnapshots] = useState(true);
  const [showShares, setShowShares] = useState(true);
  const [selectedInterval, setSelectedInterval] = useState('15m');

  // Get the current data based on selected interval
  const currentData = useMemo(() => {
    switch (selectedInterval) {
    case '1m':
      return groupMemoryData1m;
    case '5m':
      return groupMemoryData5m;
    case '30m':
      return groupMemoryData30m;
    case '15m':
    default:
      return groupMemoryData;
    }
  }, [selectedInterval, groupMemoryData, groupMemoryData1m, groupMemoryData5m, groupMemoryData30m]);

  // Transform data for chart
  const chartData = useMemo(() => {
    if (!isEmpty(currentData)) {
      return currentData.map(({
        date, users: devices, snapshots: photos, shares,
      }) => ({
        devices,
        photos,
        time: formatDateToTimezone(date, timezone, 'hh:mm a'),
        shares,
      }));
    }
    return [];
  }, [currentData, timezone]);

  const handleLegendClick = (series) => {
    if (series === 'photos') {
      setShowSnapshots(!showSnapshots);
    } else if (series === 'shares') {
      setShowShares(!showShares);
    } else {
      setShowUsers(!showUsers);
    }
  };

  const intervalOptions = [
    { value: '1m', label: '1 min' },
    { value: '5m', label: '5 mins' },
    { value: '15m', label: '15 mins' },
    { value: '30m', label: '30 mins' },
  ];

  const lineStrokeWidth = selectedInterval === '1m' ? 2 : 4;

  const intervalDropdown = (
    <Authorize permissions={CONFIG}>
      <Dropdown onSelect={setSelectedInterval}>
        <Dropdown.Toggle
          variant="link"
          size="sm"
          className="p-0 text-muted"
          style={{
            fontSize: '12px',
            textDecoration: 'none',
            border: 'none',
            boxShadow: 'none',
            fontWeight: 'normal',
          }}
        />
        <Dropdown.Menu style={{ minWidth: 'auto', padding: '4px 0' }}>
          {intervalOptions.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              active={selectedInterval === option.value}
              style={{ padding: '4px 12px', fontSize: '12px' }}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </Authorize>
  );

  if (!isEmpty(chartData)) {
    return (
      <ChartCard
        title={(
          <Info
            label="Group Memory Timeline"
            content="A visualization of the number of devices, photos, and shares during the event"
            className="ml-2"
          />
        )}
        header={intervalDropdown}
      >
        <Recharts.ResponsiveContainer width="100%" height={350}>
          <Recharts.LineChart data={chartData} isAnimationActive={false}>
            <Recharts.XAxis
              dataKey="time"
              tickLine={false}
              dy={10}
              dx={5}
              tick={{
                fontSize: '12px',
                color: '#B7B8BA',
                marginRight: '10px',
                lineHeight: '27px',
                fontWeigth: '500',
              }}
            />
            <Recharts.YAxis
              tickLine={false}
              dy={-5}
              dx={-5}
              tick={{
                fontSize: '12px',
                color: '#B7B8BA',
                marginRight: '10px',
              }}
            />
            <Recharts.Tooltip content={<ChartTooltip />} />
            <Recharts.Legend
              verticalAlign="top"
              align="right"
              wrapperStyle={{ fontSize: '14px', color: '#4E5155', textTransform: 'capitalize' }}
              iconType="circle"
              iconSize="11"
              onClick={({ dataKey }) => handleLegendClick(dataKey)}
            />
            <Recharts.Line type="monotone" dataKey="photos" stroke="var(--theme-purple)" fill="var(--theme-purple)" dot={false} strokeWidth={lineStrokeWidth} hide={!showSnapshots} />
            <Recharts.Line type="monotone" dataKey="devices" stroke="var(--theme-yellow-dark)" fill="var(--theme-yellow-dark)" dot={false} strokeWidth={lineStrokeWidth} hide={!showUsers} />
            <Recharts.Line type="monotone" dataKey="shares" stroke="var(--theme-red)" fill="var(--theme-red)" dot={false} strokeWidth={lineStrokeWidth} hide={!showShares} />
          </Recharts.LineChart>
        </Recharts.ResponsiveContainer>
      </ChartCard>
    );
  }

  return null;
};

export default GroupMemoryChart;
